import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { showToast } from '@/shared/libs/toast'
import { Button } from '@/shared/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog'
import { ImageUploadField } from '@/shared/ui/image-upload-field'

export interface TenantDestinationFormValue {
  banner: string
  thumbnail: string
}

interface TenantDestinationFormDrawerProps {
  open: boolean
  title: string
  value: TenantDestinationFormValue
  saving?: boolean
  onChange: (next: TenantDestinationFormValue) => void
  onOpenChange: (open: boolean) => void
  onSubmit: () => void
}

export function TenantDestinationFormDrawer({
  open,
  title,
  value,
  saving,
  onChange,
  onOpenChange,
  onSubmit,
}: TenantDestinationFormDrawerProps) {
  const { t } = useTranslation()
  const [isBannerUploading, setIsBannerUploading] = useState(false)
  const [isThumbnailUploading, setIsThumbnailUploading] = useState(false)
  const isUploading = isBannerUploading || isThumbnailUploading

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('destination.bannerThumbnailOptionalHint')}
          </p>
          <ImageUploadField
            label={t('destination.banner')}
            value={value.banner}
            onChange={(banner) => onChange({ ...value, banner })}
            texts={{
              select: t('destination.selectImage'),
              uploading: t('destination.uploadingImage'),
              empty: t('destination.bannerEmpty'),
              previewAlt: t('destination.banner'),
            }}
            disabled={saving ?? false}
            onUploadingChange={setIsBannerUploading}
            onUploadSuccess={() => showToast.success(t('destination.imageUploadSuccess'))}
            onUploadError={(message) => showToast.error(message)}
            uploadFailedText={t('destination.imageUploadFailed')}
          />
          <ImageUploadField
            label={t('destination.thumbnail')}
            value={value.thumbnail}
            onChange={(thumbnail) => onChange({ ...value, thumbnail })}
            texts={{
              select: t('destination.selectImage'),
              uploading: t('destination.uploadingImage'),
              empty: t('destination.thumbnailEmpty'),
              previewAlt: t('destination.thumbnail'),
            }}
            disabled={saving ?? false}
            onUploadingChange={setIsThumbnailUploading}
            onUploadSuccess={() => showToast.success(t('destination.imageUploadSuccess'))}
            onUploadError={(message) => showToast.error(message)}
            uploadFailedText={t('destination.imageUploadFailed')}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={saving} onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={Boolean(saving) || isUploading}
            onClick={onSubmit}
            title={isUploading ? t('destination.waitForUpload') : undefined}
            aria-busy={saving}
          >
            {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
